import type { ImportItemType } from "@lafayette/import-mapping";

import { columnLabel, type ImportColumnView } from "./columns";
import { type ColumnDecisions, mappingProblems } from "./decisions";
import { mappingIssueMessages } from "./labels";

// Why "Submit for validation" is blocked. The list is worked out from the same rules the server
// applies at submit (`validateMapping` with the `submit` stage), plus the two rules of the screen:
// the draft must be saved, and it must have no unsaved change (the server submits what it holds).

export type SubmitBlocker = Readonly<{
  id: string;
  text: string;
  /** The column the staff member can go to; `null` when the problem is not about one column. */
  columnIndex: number | null;
}>;

export function submitBlockers(
  input: Readonly<{
    hasJob: boolean;
    dirty: boolean;
    decisions: ColumnDecisions;
    defaultItemType: ImportItemType | null;
    columns: readonly ImportColumnView[];
  }>,
): readonly SubmitBlocker[] {
  const blockers: SubmitBlocker[] = [];
  if (!input.hasJob) {
    blockers.push({
      id: "unsaved-draft",
      text: "Save the draft first. Submit sends the saved mapping.",
      columnIndex: null,
    });
  } else if (input.dirty) {
    blockers.push({
      id: "unsaved-changes",
      text: "You have unsaved changes. Save the draft first.",
      columnIndex: null,
    });
  }
  const labelOf = (columnIndex: number) => {
    const column = input.columns.find((entry) => entry.index === columnIndex);
    return column === undefined ? `Column ${columnIndex + 1}` : columnLabel(column);
  };
  for (const issue of mappingProblems(input.decisions, input.defaultItemType, "submit")) {
    const id = `${issue.code}-${issue.columnIndex ?? "job"}`;
    if (issue.code === "COLUMN_UNDECIDED" && issue.columnIndex !== null) {
      blockers.push({
        id,
        text: `${labelOf(issue.columnIndex)} is not mapped. Map it or ignore it.`,
        columnIndex: issue.columnIndex,
      });
    } else if (issue.columnIndex !== null) {
      blockers.push({
        id,
        text: `${labelOf(issue.columnIndex)}: ${mappingIssueMessages[issue.code]}`,
        columnIndex: issue.columnIndex,
      });
    } else {
      blockers.push({ id, text: mappingIssueMessages[issue.code], columnIndex: null });
    }
  }
  return blockers;
}
